// ArtistAvatar.jsx — the one renderer for artist avatars, everywhere an
// artist's identity appears (identity badge, practice card, artist cards,
// dossier). Avatar data lives at artists/{id}.avatar:
//   { base: "🦊", bg: "#ef537e",
//     hat: "👑 Griddle Crown" | null,      // worn on top
//     held: "🥄 Wooden Spoon" | null,      // bottom-right mini-badge
//     accessory: "⭐ Gold Star Pin" | null } // bottom-left mini-badge
// (legacy single-slot field `gear` reads as `held`.)
// Items are cosmetic and come from the unlock catalog in utils/practice.js
// (rank gear + badge prizes) — the equip UI only offers earned items.
// Frame (silver/gold/legend ring) is DERIVED from XP where the caller knows
// it; callers without XP context (identity badge) simply pass no frame.

import { STAFF_THEME } from "../theme/staff";

const BRAND = STAFF_THEME;

export const AVATAR_BASES = [
  "🥞", "😀", "😎", "🤠", "🥳", "😺", "🦊", "🐻", "🐼", "🐨", "🐸", "🐙",
  "🦄", "🐝", "🦉", "🐢", "🐰", "🐯", "🦁", "🦖", "🐳", "🌞", "🌙", "⭐",
];

export const AVATAR_BGS = [
  "#1f59db", "#4aa4e1", "#1d52a0", "#f57600", "#fcb31f", "#ef537e", "#7c5cd6", "#2e8b57",
];

const FRAME_STYLES = {
  silver: "0 0 0 2.5px #c0c8d8",
  gold: "0 0 0 2.5px #fcb31f",
  legend: "0 0 0 3px #fcb31f, 0 0 10px 2px rgba(252,179,31,0.55)",
};

const iconOf = (item) => (item ? item.split(" ")[0] : null);

export default function ArtistAvatar({ avatar, fallbackEmoji, size = 40, frame = null }) {
  const base = avatar?.base || fallbackEmoji || "🥞";
  const bg = avatar?.bg || BRAND.raised;
  const hat = iconOf(avatar?.hat);
  const held = iconOf(avatar?.held || avatar?.gear); // legacy gear = held
  const accessory = iconOf(avatar?.accessory);

  const miniBadge = (side) => ({
    position: "absolute", [side]: -size * 0.06, bottom: -size * 0.06,
    width: size * 0.42, height: size * 0.42, borderRadius: "50%",
    background: "#0d1833", border: "1.5px solid rgba(255,255,255,0.25)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: size * 0.24, lineHeight: 1,
  });

  return (
    <div style={{
      position: "relative", width: size, height: size, flexShrink: 0,
      borderRadius: "50%", background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: frame ? FRAME_STYLES[frame] : "0 0 0 1.5px rgba(255,255,255,0.15)",
    }}>
      <span style={{ fontSize: size * 0.55, lineHeight: 1 }}>{base}</span>
      {hat && (
        <span style={{
          position: "absolute", top: -size * 0.28, left: "50%",
          transform: "translateX(-50%)",
          fontSize: size * 0.4, lineHeight: 1,
          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
          pointerEvents: "none",
        }}>
          {hat}
        </span>
      )}
      {held && <span style={miniBadge("right")}>{held}</span>}
      {accessory && <span style={miniBadge("left")}>{accessory}</span>}
    </div>
  );
}

export function avatarFrameForXp(xp) {
  if (xp >= 17000) return "legend";
  if (xp >= 1600) return "gold";
  if (xp >= 1100) return "silver";
  return null;
}
